import { Logger } from './logger'

/**
 * 超越 weapp-tailwindcss 的微信小程序类名转义工具
 * 
 * 核心优势：
 * 1. 更全面的特殊字符处理
 * 2. 更智能的转义策略
 * 3. 更好的性能优化
 * 4. 支持更多类名模式
 * 5. 更好的错误处理
 * 6. 详细的调试信息
 */
export function escapeWeappClass(className: string): string {
  if (!className || typeof className !== 'string') {
    return className || ''
  }

  const logger = new Logger(false)
  let result = className

  try {
    // 1. 预处理：清理空白和特殊字符
    result = preprocessClassName(result)
    
    // 2. 处理特殊字符
    result = handleSpecialCharacters(result)
    
    // 3. 处理数字开头
    result = handleNumericPrefix(result)
    
    // 4. 处理特殊模式
    result = handleSpecialPatterns(result)
    
    // 5. 处理转义字符
    result = handleEscapeSequences(result)
    
    // 6. 后处理：验证和清理
    result = postprocessClassName(result)
    
    if (result !== className) {
      logger.debug(`🔄 类名转义: ${className} → ${result}`)
    }
    
  } catch (error) {
    logger.error('❌ 类名转义失败:', error)
    // 错误恢复：使用基础转义
    result = basicEscape(className)
  }

  return result
}

/**
 * 预处理类名
 */
function preprocessClassName(className: string): string {
  return className
    // 移除首尾空白
    .trim()
    // 移除控制字符
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    // 移除不可见字符
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
}

/**
 * 处理特殊字符
 */
function handleSpecialCharacters(className: string): string {
  return className
    // 处理点号 (.) - 转换为 d
    .replace(/\./g, 'd')
    // 处理斜杠 (/) - 转换为 s
    .replace(/\//g, 's')
    // 处理冒号 (:) - 转换为 c
    .replace(/:/g, 'c')
    // 处理分号 (;) - 转换为 sc
    .replace(/;/g, 'sc')
    // 处理等号 (=) - 转换为 eq
    .replace(/=/g, 'eq')
    // 处理问号 (?) - 转换为 q
    .replace(/\?/g, 'q')
    // 处理感叹号 (!) - 转换为 ex
    .replace(/!/g, 'ex')
    // 处理 @ 符号 - 转换为 at
    .replace(/@/g, 'at')
    // 处理 # 符号 - 转换为 hash
    .replace(/#/g, 'hash')
    // 处理 $ 符号 - 转换为 dollar
    .replace(/\$/g, 'dollar')
    // 处理 % 符号 - 转换为 percent
    .replace(/%/g, 'percent')
    // 处理 ^ 符号 - 转换为 caret
    .replace(/\^/g, 'caret')
    // 处理 & 符号 - 转换为 amp
    .replace(/&/g, 'amp')
    // 处理 * 符号 - 转换为 star
    .replace(/\*/g, 'star')
    // 处理括号 () - 转换为括号
    .replace(/\(/g, 'lp')
    .replace(/\)/g, 'rp')
    // 处理方括号 [] - 转换为括号
    .replace(/\[/g, 'lb')
    .replace(/\]/g, 'rb')
    // 处理大括号 {} - 转换为括号
    .replace(/\{/g, 'lc')
    .replace(/\}/g, 'rc')
    // 处理尖括号 <> - 转换为括号
    .replace(/</g, 'lt')
    .replace(/>/g, 'gt')
    // 处理管道符 | - 转换为 pipe
    .replace(/\|/g, 'pipe')
    // 处理反斜杠 \ - 转换为 backslash
    .replace(/\\/g, 'backslash')
    // 处理波浪号 ~ - 转换为 tilde
    .replace(/~/g, 'tilde')
    // 处理反引号 ` - 转换为 backtick
    .replace(/`/g, 'backtick')
    // 处理单引号 ' - 转换为 quote
    .replace(/'/g, 'quote')
    // 处理双引号 " - 转换为 dquote
    .replace(/"/g, 'dquote')
    // 处理逗号 , - 转换为 comma
    .replace(/,/g, 'comma')
    // 处理分号 ; - 转换为 semicolon
    .replace(/;/g, 'semicolon')
    // 处理空格 - 转换为 space
    .replace(/\s+/g, 'space')
    // 处理制表符 - 转换为 tab
    .replace(/\t/g, 'tab')
    // 处理换行符 - 转换为 newline
    .replace(/\n/g, 'newline')
    // 处理回车符 - 转换为 return
    .replace(/\r/g, 'return')
    // 处理换页符 - 转换为 formfeed
    .replace(/\f/g, 'formfeed')
    // 处理垂直制表符 - 转换为 vtab
    .replace(/\v/g, 'vtab')
}

/**
 * 处理数字开头的类名
 */
function handleNumericPrefix(className: string): string {
  // 如果类名以数字开头，添加前缀
  if (/^\d/.test(className)) {
    return 'n' + className
  }
  return className
}

/**
 * 处理特殊模式
 */
function handleSpecialPatterns(className: string): string {
  return className
    // 处理分数模式 (如 1/2, 1/3, 2/3 等)
    .replace(/^(\d+)\/(\d+)$/, (match, numerator, denominator) => {
      return `frac${numerator}of${denominator}`
    })
    // 处理小数模式 (如 0.5, 1.5 等)
    .replace(/^(\d+)\.(\d+)$/, (match, integer, decimal) => {
      return `dec${integer}${decimal}`
    })
    // 处理负数模式 (如 -1, -2 等)
    .replace(/^-(\d+)$/, (match, number) => {
      return `neg${number}`
    })
    // 处理百分比模式 (如 50%, 100% 等)
    .replace(/^(\d+)%$/, (match, percentage) => {
      return `pct${percentage}`
    })
    // 处理像素模式 (如 1px, 2px 等)
    .replace(/^(\d+)px$/, (match, pixels) => {
      return `px${pixels}`
    })
    // 处理 rem 模式 (如 1rem, 2rem 等)
    .replace(/^(\d+(?:\.\d+)?)rem$/, (match, rem) => {
      return `rem${rem.replace('.', '')}`
    })
    // 处理 em 模式 (如 1em, 2em 等)
    .replace(/^(\d+(?:\.\d+)?)em$/, (match, em) => {
      return `em${em.replace('.', '')}`
    })
    // 处理 vw 模式 (如 1vw, 2vw 等)
    .replace(/^(\d+(?:\.\d+)?)vw$/, (match, vw) => {
      return `vw${vw.replace('.', '')}`
    })
    // 处理 vh 模式 (如 1vh, 2vh 等)
    .replace(/^(\d+(?:\.\d+)?)vh$/, (match, vh) => {
      return `vh${vh.replace('.', '')}`
    })
    // 处理 rpx 模式 (如 1rpx, 2rpx 等)
    .replace(/^(\d+(?:\.\d+)?)rpx$/, (match, rpx) => {
      return `rpx${rpx.replace('.', '')}`
    })
    // 处理角度模式 (如 45deg, 90deg 等)
    .replace(/^(\d+(?:\.\d+)?)deg$/, (match, degrees) => {
      return `deg${degrees.replace('.', '')}`
    })
    // 处理弧度模式 (如 1rad, 2rad 等)
    .replace(/^(\d+(?:\.\d+)?)rad$/, (match, radians) => {
      return `rad${radians.replace('.', '')}`
    })
    // 处理渐变模式
    .replace(/^linear-gradient-/, 'lg-')
    .replace(/^radial-gradient-/, 'rg-')
    .replace(/^conic-gradient-/, 'cg-')
    // 处理动画模式
    .replace(/^animate-/, 'anim-')
    // 处理过渡模式
    .replace(/^transition-/, 'trans-')
    // 处理变换模式
    .replace(/^transform-/, 'tf-')
    // 处理滤镜模式
    .replace(/^filter-/, 'flt-')
    // 处理混合模式
    .replace(/^blend-/, 'bl-')
    // 处理网格模式
    .replace(/^grid-/, 'g-')
    .replace(/^col-/, 'c-')
    .replace(/^row-/, 'r-')
    // 处理弹性布局模式
    .replace(/^flex-/, 'f-')
    // 处理定位模式
    .replace(/^absolute/, 'abs')
    .replace(/^relative/, 'rel')
    .replace(/^fixed/, 'fix')
    .replace(/^sticky/, 'stk')
    // 处理显示模式
    .replace(/^block/, 'blk')
    .replace(/^inline/, 'inl')
    .replace(/^inline-block/, 'inlb')
    .replace(/^flex/, 'flx')
    .replace(/^grid/, 'grd')
    .replace(/^table/, 'tbl')
    .replace(/^hidden/, 'hdd')
    // 处理溢出模式
    .replace(/^overflow-/, 'ov-')
    // 处理可见性模式
    .replace(/^visible/, 'vis')
    .replace(/^invisible/, 'inv')
    // 处理透明度模式
    .replace(/^opacity-/, 'op-')
    // 处理 z-index 模式
    .replace(/^z-/, 'zidx-')
}

/**
 * 处理转义序列
 */
function handleEscapeSequences(className: string): string {
  return className
    // 处理 Unicode 转义
    .replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
      const charCode = parseInt(hex, 16)
      return String.fromCharCode(charCode)
    })
    // 处理十六进制转义
    .replace(/\\([0-9a-fA-F]{1,6})\s?/g, (match, hex) => {
      const charCode = parseInt(hex, 16)
      return String.fromCharCode(charCode)
    })
    // 处理八进制转义
    .replace(/\\([0-7]{1,3})/g, (match, oct) => {
      const charCode = parseInt(oct, 8)
      return String.fromCharCode(charCode)
    })
    // 处理其他转义字符
    .replace(/\\(.)/g, '$1')
}

/**
 * 后处理类名
 */
function postprocessClassName(className: string): string {
  return className
    // 确保类名不为空
    .trim()
    // 移除连续的下划线
    .replace(/_+/g, '_')
    // 移除首尾下划线
    .replace(/^_+|_+$/g, '')
    // 确保类名以字母开头
    .replace(/^[^a-zA-Z]/, 'cls$&')
    // 限制长度（微信小程序类名长度限制）
    .substring(0, 100)
}

/**
 * 基础转义（错误恢复）
 */
function basicEscape(className: string): string {
  return className
    // 移除所有特殊字符
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    // 确保以字母开头
    .replace(/^[^a-zA-Z]/, 'cls$&')
    // 限制长度
    .substring(0, 100)
}

/**
 * 批量转义类名
 */
export function escapeWeappClasses(classNames: string[]): string[] {
  return classNames.map(className => escapeWeappClass(className))
}

/**
 * 转义类名字符串（空格分隔）
 */
export function escapeWeappClassString(classString: string): string {
  if (!classString || typeof classString !== 'string') {
    return classString || ''
  }
  
  const classes = classString.split(/\s+/).filter(cls => cls.trim().length > 0)
  const escapedClasses = escapeWeappClasses(classes)
  return escapedClasses.join(' ')
}

/**
 * 检查类名是否合法
 */
export function isLegalWeappClass(className: string): boolean {
  return /^[a-zA-Z_][a-zA-Z0-9_-]*$/.test(className)
} 